import React, {
  useMemo,
  useEffect,
  useState,
  useCallback,
} from 'react';

import { useRouter } from 'next/router';
import styled, { withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import moment from '@g4/moment-utc';
import { withValue } from '@g4/automation-hooks';
import get from 'lodash/get';
import tracking from '@g4/g4-analytics';

import {
  Tab,
  Tabs,
  Text,
  Box,
  Spinner,
  useMedia,
  Layout,
} from '../../../components/Boneyard';
import Markdown from '../../../components/Markdown';
import getDatesWithLowFares from './flightChooser/getDatesWithLowFares';
import FlightsList from './flightChooser/FlightsList';
import DayTab from './flightChooser/DayTab';
import StadiumDayTab from './flightChooser/StadiumDayTab';

import {
  DAYS_TABS,
  DAY_TAB,
  PREVIOUS_ARROW,
  NEXT_ARROW,
  FLIGHT_CHOOSER_SPINNER,
  FLIGHTS_ROUND_TRIP_DISCOUNT,
} from '../../../../automationHooks.json';
import { tripTypeValues, DEPARTING, RETURNING, eventTypes } from '../constants';

import { useTranslations } from '../../../graphql/components/TranslationsProvider';
import PLANE_ICON_LEFT from '../../../images/pattern-plane-path-top-left.svg';
import PLANE_ICON_RIGHT from '../../../images/pattern-plane-path-top-right.svg';
import PLANE_ICON_BOTTOM from '../../../images/pattern-plane-path-bottom-left.svg';
import { useBookingPath, useCart } from '../../../graphql';
import useStadiumGame from '../../../hooks/useStadiumGame';
import getFlightsRawProducts from '../../../pageComponents/indexPage/getFlightsRawProducts';
import { getAffinityString } from '../../../pageComponents/app/trackingItems';
import { sortOrder } from '../../../utils/constants/trackingConstants';
import { getBundleRawProduct } from '../../bundlesPage/getBundleRawProducts';

const propTypes = {
  leg: PropTypes.oneOf(['departing', 'returning']).isRequired,
  flights: PropTypes.array.isRequired,
  selectedTabDate: PropTypes.string.isRequired,
  middleTabDate: PropTypes.string.isRequired,
  selectedFlightOption: PropTypes.object,
  setSelectedFlight: PropTypes.func,
  flightSearchCriteria: PropTypes.object,
  numberOfPassengers: PropTypes.number,
  setSelectedTabDate: PropTypes.func,
  setReturningTabsWithoutClearingWarning: PropTypes.func,
  lastAvailableDate: PropTypes.string,
  earliestAvailableDate: PropTypes.string,
  isFetchingMoreFlights: PropTypes.bool,
  invalidState: PropTypes.shape({
    displayOn: PropTypes.string,
    disable: PropTypes.string,
  }),
  oppositeLegSelection: PropTypes.object,
  automationHook: PropTypes.string,
  shouldAutoSelect: PropTypes.bool.isRequired,
  setShouldAutoSelect: PropTypes.func.isRequired,
  onArrowClick: PropTypes.func.isRequired,
  oppositeSelectedTabDate: PropTypes.string,
  shouldReturnFlightOptionBeNull: PropTypes.bool.isRequired,
  setShouldReturnFlightOptionBeNull: PropTypes.func.isRequired,
  warning: PropTypes.object,
  deactivateWarning: PropTypes.func,
  loyaltyPointsValue: PropTypes.number,
  theme: PropTypes.object,
  lowFareQuery: PropTypes.shape({
    lowFares: PropTypes.array,
    loading: PropTypes.bool,
  }),
  isRedemptionEnabledForNavitaire: PropTypes.bool,
  googleAnalyticsData: PropTypes.object,
  selectedBundles: PropTypes.object,
  onBundleSelect: PropTypes.func,
  bundleOffers: PropTypes.array,
  allAncillariesList: PropTypes.array,
};

const defaultProps = {
  setSelectedTabDate: Function.prototype,
};

const NEXT = 'next';
const PREVIOUS = 'previous';
const trackedViewItemListKeys = new Set();

const DisclaimerText = styled(Text)`
  text-align: center;
  ${props => props.theme.query.md} {
    text-align: right;
  }
`;

const Constrain = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 calc(${props => props.theme.spacing(6)} / 2);
  position: relative;

  ${props =>
    props.leg === 'departing'
      ? `&::before,
  &::after {
    content: '';
    position: absolute;
    left: -100px;
    bottom: calc(50% - 90px);
    background: url(${PLANE_ICON_LEFT}) no-repeat;
    background-size: cover;
    background-position: center top;
    width: 90px;
    height: 90px;
    display: block;
    z-index: -1;
  }

  &::after {
    background: url(${PLANE_ICON_RIGHT}) no-repeat;
    width: 474px;
    height: 114px;
    left: auto;
    right: -100px;
    bottom: calc(50% - 114px);
  }`
      : `
  &::after {
    content: '';
    position: absolute;
    display: block;
    background: url(${PLANE_ICON_BOTTOM}) no-repeat;
    background-size: cover;
    background-position: center top;
    width: 559px;
    height: 583px;
    left: -50px;
    bottom: calc(50% - 500px);
    z-index: -1;
  }
  `}
`;

const FlightChooser = React.memo(
  ({
    leg,
    flights,
    lowFareQuery,
    oppositeLegSelection,
    invalidState,
    selectedTabDate,
    middleTabDate,
    selectedFlightOption,
    setSelectedFlight,
    lastAvailableDate,
    setSelectedTabDate,
    setReturningTabsWithoutClearingWarning,
    flightSearchCriteria,
    numberOfPassengers,
    isFetchingMoreFlights,
    earliestAvailableDate,
    automationHook,
    shouldAutoSelect,
    setShouldAutoSelect,
    onArrowClick,
    oppositeSelectedTabDate,
    shouldReturnFlightOptionBeNull,
    setShouldReturnFlightOptionBeNull,
    warning,
    deactivateWarning,
    loyaltyPointsValue,
    theme,
    isRedemptionEnabledForNavitaire,
    googleAnalyticsData,
    selectedBundles,
    onBundleSelect,
    bundleOffers,
    allAncillariesList,
  }) => {
    const mq = useMedia();
    const { isStadium } = useBookingPath();
    const [stadiumGame] = useStadiumGame();
    const { cart } = useCart();
    const isMobile = mq.lte('sm');
    const translations = useTranslations();
    const isRoundTripWithDiscount =
      flightSearchCriteria.tripType === tripTypeValues.ROUNDTRIP &&
      get(flights, '[0].totalDiscountValue');
    const raidersGameDate = moment(stadiumGame?.date).format('YYYY-MM-DD');

    const [isRaidersBooking, setIsRaidersBooking] = useState(false);

    // Dedupe GA4 list-view tracking by leg + search context + selected tab date.
    // The key store is module-scoped so remounts on initial load do not double-fire.
    const trackingKey = [
      leg,
      selectedTabDate,
      flightSearchCriteria?.tripType,
      flightSearchCriteria?.origin,
      flightSearchCriteria?.destination,
      flightSearchCriteria?.departDate?.date,
      flightSearchCriteria?.returnDate?.date || '',
    ].join('|');

    // Controls the functionality of the navigation arrows rendered in the low-fare tabs
    const datesWithLowFares = useMemo(() => {
      return getDatesWithLowFares(
        leg,
        oppositeLegSelection,
        flights,
        lowFareQuery.lowFares,
        middleTabDate,
        oppositeSelectedTabDate,
        isMobile ? 1 : 3
      );
    }, [
      leg,
      oppositeLegSelection,
      flights,
      middleTabDate,
      oppositeSelectedTabDate,
      isMobile,
      lowFareQuery.lowFares,
    ]);

    const disableDayTab = useCallback(
      (lastAvailableDate, date) => {
        if (isRaidersBooking) {
          if (leg === DEPARTING) {
            return (
              moment(raidersGameDate).isSame(moment(date)) ||
              moment(date).isAfter(moment(raidersGameDate))
            );
          }

          if (leg === RETURNING) {
            return (
              moment(raidersGameDate).isSame(moment(date)) ||
              moment(date).isBefore(moment(raidersGameDate))
            );
          }
        }

        return moment(date).isAfter(moment(lastAvailableDate), 'day');
      },
      [isRaidersBooking, leg, raidersGameDate]
    );

    useEffect(() => {
      if (isStadium) {
        setIsRaidersBooking(true);
      }
    }, [isStadium]);

    useEffect(() => {
      if (leg === RETURNING && shouldReturnFlightOptionBeNull === true) {
        setSelectedFlight(null);
        setShouldReturnFlightOptionBeNull(false);
      }
    }, [
      leg,
      shouldReturnFlightOptionBeNull,
      setSelectedFlight,
      setShouldReturnFlightOptionBeNull,
    ]);

    const router = useRouter();
    const { transactionId } = router.query;

    // GA4 view_item_list tracking
    useEffect(() => {
      const flightsForTab = datesWithLowFares[selectedTabDate]?.flights || [];
      const priceOfCheapestFlight =
        datesWithLowFares[selectedTabDate]?.fare?.salesTotal;
      const isTabDisabled =
        !(priceOfCheapestFlight || lowFareQuery.loading) ||
        disableDayTab(lastAvailableDate, selectedTabDate);
      
      // Only track if tab is enabled, has flights, has GA data, and hasn't been tracked yet
      if (
        !isTabDisabled &&
        flightsForTab.length > 0 &&
        googleAnalyticsData?.data &&
        transactionId &&
        !trackedViewItemListKeys.has(trackingKey)
      ) {
        const affinityString = getAffinityString(cart?.order);
        const flightRawProducts = flightsForTab.flatMap((flight) => 
          getFlightsRawProducts(
            { [leg]: flight },
            flightSearchCriteria,
            loyaltyPointsValue,
            affinityString,
            googleAnalyticsData.data.application.googleAnalytics.flightMarkets,
            leg,
            eventTypes.LIST_VIEW
          )
        );
        
        const bundleRawProducts = [];
        flightsForTab.forEach((flight) => {
          if (flight.bundleReferences?.length > 0) {
            flight.flightFares.forEach((fare) => {
              fare.bundleReferences.forEach((reference) => {
                const bundleOffer = bundleOffers?.find(
                  (bundle) => bundle.bundleReference === reference
                );
                if (bundleOffer) {
                  bundleRawProducts.push(
                    getBundleRawProduct(
                      bundleOffer,
                      flightSearchCriteria,
                      affinityString,
                      flight.flight
                    ).items
                  );
                }
              });
            });
          }
        });
        
        const rawProducts = [...flightRawProducts, ...bundleRawProducts];
        
        tracking.setDataLayerVars({ search_manifest: transactionId });

        tracking.trackViewItemList('view_item_list', {
          name: sortOrder.DEFAULT,
          rawProducts: rawProducts,
        });

        trackedViewItemListKeys.add(trackingKey);
      }
    }, [
      selectedTabDate,
      datesWithLowFares,
      trackingKey,
      transactionId,
      flightSearchCriteria,
      loyaltyPointsValue,
      cart?.order,
      googleAnalyticsData,
      leg,
      lastAvailableDate,
      lowFareQuery.loading,
      bundleOffers,
      disableDayTab,
    ]);

    const maxLimitDate =
      isRaidersBooking && leg === DEPARTING
        ? raidersGameDate
        : lastAvailableDate;
    const minLimitDate =
      isRaidersBooking && leg === RETURNING
        ? raidersGameDate
        : earliestAvailableDate;

    const arrowsDisabledStatus = useMemo(
      () => ({
        [NEXT]:
          lowFareQuery.loading ||
          Object.keys(datesWithLowFares).includes(maxLimitDate),
        [PREVIOUS]:
          lowFareQuery.loading ||
          (leg === RETURNING &&
            oppositeLegSelection &&
            moment(
              oppositeLegSelection?.flight.departingTime,
              'YYYY-MM-DD'
            ).diff(moment(Object.keys(datesWithLowFares)[0], 'YYYY-MM-DD')) >=
              0) ||
          (leg === RETURNING &&
            oppositeSelectedTabDate &&
            moment(oppositeSelectedTabDate, 'YYYY-MM-DD').diff(
              moment(Object.keys(datesWithLowFares)[0], 'YYYY-MM-DD')
            ) >= 0) ||
          Object.keys(datesWithLowFares).includes(minLimitDate) ||
          Object.keys(datesWithLowFares).includes(
            moment().format('YYYY-MM-DD')
          ),
      }),
      [
        leg,
        minLimitDate,
        maxLimitDate,
        datesWithLowFares,
        oppositeLegSelection,
        oppositeSelectedTabDate,
        lowFareQuery.loading,
      ]
    );
    const handOnChange = useCallback(
      (index, tabProps, donotClearWarning) => {
        if (donotClearWarning) {
          tabProps &&
            setReturningTabsWithoutClearingWarning({ selected: tabProps.date });
          if (leg === RETURNING) {
            setShouldReturnFlightOptionBeNull(true);
          }
          setShouldAutoSelect(prevState => ({ ...prevState, [leg]: false }));
        } else {
          tabProps && setSelectedTabDate({ selected: tabProps.date });
          if (leg === RETURNING) {
            setShouldReturnFlightOptionBeNull(false);
          }
          setShouldAutoSelect(prevState => ({ ...prevState, [leg]: true }));
        }
      },
      [
        setShouldAutoSelect,
        setSelectedTabDate,
        leg,
        setShouldReturnFlightOptionBeNull,
        setReturningTabsWithoutClearingWarning,
      ]
    );

    return (
      <div data-hook={automationHook}>
        <Tabs
          withNavigationArrows={true}
          defaultTab={Object.keys(datesWithLowFares).indexOf(selectedTabDate)}
          onArrowClick={onArrowClick}
          arrowsDisabledStatus={arrowsDisabledStatus}
          tabsWrapperHook={withValue(DAYS_TABS.hook, leg)}
          leftArrowAutomationHook={withValue(PREVIOUS_ARROW.hook, leg)}
          rightArrowAutomationHook={withValue(NEXT_ARROW.hook, leg)}
          onChange={handOnChange}
          doNotShowBottomBorderUnderTabs={isMobile || !isRoundTripWithDiscount}
          leg={leg}
        >
          {Object.keys(datesWithLowFares).map((date, index) => {
            const flightsOnDate = datesWithLowFares[date]?.flights;
            const priceOfCheapestFlight =
              datesWithLowFares[date]?.fare?.salesTotal;
            const disabled = !(priceOfCheapestFlight || lowFareQuery.loading);
            const disabledDayTab = disableDayTab(lastAvailableDate, date);
            return (
              <Tab
                label={
                  // Using isStadium here instead of isRaidersBooking
                  // Because isRaidersBooking is a state therefore there is a delay
                  !isStadium ? (
                    <DayTab
                      isTabLoading={lowFareQuery.loading}
                      invalidState={
                        invalidState && invalidState.displayOn === leg
                          ? invalidState
                          : null
                      }
                      tabPosition={
                        index === Object.keys(datesWithLowFares).length - 1
                          ? 'last'
                          : (!index && 'first') || null
                      }
                      date={date}
                      priceOfCheapestFlight={priceOfCheapestFlight}
                    />
                  ) : (
                    <StadiumDayTab
                      isTabLoading={lowFareQuery.loading}
                      invalidState={
                        invalidState && invalidState.displayOn === leg
                          ? invalidState
                          : null
                      }
                      tabPosition={
                        index === Object.keys(datesWithLowFares).length - 1
                          ? 'last'
                          : (!index && 'first') || null
                      }
                      date={date}
                      priceOfCheapestFlight={priceOfCheapestFlight}
                      raidersGameDate={raidersGameDate}
                      flightsOnDate={!!priceOfCheapestFlight}
                      isRaidersBooking={isRaidersBooking}
                      gameDayLabel={translations.stadiumPackages.gameDayLabel}
                      disabledDayTab={disabledDayTab}
                    />
                  )
                }
                key={date}
                id={date}
                contentId={date}
                // Needed to make a reselection in Tabs when
                // landing on a tab with no flights.
                date={date}
                disabled={disabled || disabledDayTab}
                automationHook={withValue(
                  DAY_TAB.hook,
                  `${leg}_${moment(date).format('YYYY-MM-DD')}`
                )}
              >
                {isFetchingMoreFlights || lowFareQuery.loading ? (
                  <Box d={'flex'} justifyContent={'center'}>
                    <Spinner
                      size="large"
                      inline={true}
                      automationHook={FLIGHT_CHOOSER_SPINNER.hook}
                    />
                  </Box>
                ) : (
                  <>
                    {isRoundTripWithDiscount && (
                      <Box
                        d="flex"
                        justifyContent={mq.lt('md') ? 'center' : 'flex-end'}
                        pb={2}
                        pt={2}
                      >
                        <DisclaimerText
                          size={mq.lt('sm') ? 12 : 15}
                          automationHook={withValue(
                            FLIGHTS_ROUND_TRIP_DISCOUNT.hook,
                            leg,
                            'roundtrip-disclaimer'
                          )}
                          weight={600}
                          color={theme.colors.common.textSecondary}
                        >
                          {
                            <Markdown
                              source={
                                translations.flightsPage
                                  .roundTripDiscountDisclaimerText
                              }
                            />
                          }
                        </DisclaimerText>
                      </Box>
                    )}
                    <Layout.FullWidth>
                      <Constrain leg={leg}>
                        <FlightsList
                          isFlightsQueryLoading={isFetchingMoreFlights}
                          flights={flightsOnDate}
                          selectedFlightOption={selectedFlightOption}
                          setSelectedFlight={setSelectedFlight}
                          leg={leg}
                          date={date}
                          oppositeLegSelection={oppositeLegSelection}
                          invalidState={invalidState}
                          shouldAutoSelect={shouldAutoSelect}
                          setShouldAutoSelect={setShouldAutoSelect}
                          numberOfPassengers={numberOfPassengers}
                          warning={warning}
                          deactivateWarning={deactivateWarning}
                          loyaltyPointsValue={loyaltyPointsValue}
                          isRedemptionEnabledForNavitaire={
                            isRedemptionEnabledForNavitaire
                          }
                          flightSearchCriteria={flightSearchCriteria}
                          googleAnalyticsData={googleAnalyticsData}
                          selectedBundles={selectedBundles}
                          onBundleSelect={onBundleSelect}
                          bundleOffers={bundleOffers}
                          allAncillariesList={allAncillariesList}
                          isStadium={isStadium}
                        />
                      </Constrain>
                    </Layout.FullWidth>
                  </>
                )}
              </Tab>
            );
          })}
        </Tabs>
      </div>
    );
  }
);

FlightChooser.displayName = 'FlightChooser';
FlightChooser.propTypes = propTypes;
FlightChooser.defaultProps = defaultProps;

export default withTheme(FlightChooser);
